/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
// import { InferenceSession, Tensor, env } from 'onnxruntime-web';
// env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.8.2-dev.20210831.0/dist/';

// eslint-disable-next-line import/extensions
import * as ort from 'onnxruntime-web/dist/ort-web.min.js';

// eslint-disable-next-line import/no-unresolved
import wasm from 'onnxruntime-web/dist/ort-wasm.wasm?url';
// eslint-disable-next-line import/no-unresolved
import wasmThreaded from 'onnxruntime-web/dist/ort-wasm-threaded.wasm?url';
// eslint-disable-next-line import/no-unresolved
import wasmSimd from 'onnxruntime-web/dist/ort-wasm-simd.wasm?url';
// eslint-disable-next-line import/no-unresolved
import wasmSimdThreaded from 'onnxruntime-web/dist/ort-wasm-simd-threaded.wasm?url';
import getMFEPath from './getMFEPath';

const { InferenceSession, Tensor } = ort;

ort.env.wasm.wasmPaths = {
  'ort-wasm.wasm': getMFEPath(wasm),
  'ort-wasm-threaded.wasm': getMFEPath(wasmThreaded),
  'ort-wasm-simd.wasm': getMFEPath(wasmSimd),
  'ort-wasm-simd-threaded.wasm': getMFEPath(wasmSimdThreaded),
};

function intersection(roiA, roiB) {
  const left = Math.min(roiA.x, roiB.x);
  const right = Math.max(roiA.x + roiA.w, roiB.x + roiB.w);
  const up = Math.min(roiA.y, roiB.y);
  const bottom = Math.max(roiA.y + roiA.h, roiB.y + roiB.h);
  return (right - left) * (bottom - up);
}

function iou(roiA, roiB) {
  return roiA.w * roiA.h + roiB.w * roiB.h - intersection(roiA, roiB);
}

function classNms(detections, nms_thr) {
  // eslint-disable-next-line max-len
  const detectionsFilterByScore = detections
    .filter((det) => det.score > 0)
    .sort((det1, det2) => det2.score - det1.score);
  const detectionsFilterByIOU = detectionsFilterByScore.filter((det, detIndex) => {
    if (!det.visited) {
      // eslint-disable-next-line no-plusplus
      for (let delIndex = detIndex + 1; delIndex < detectionsFilterByScore.length; ++delIndex) {
        // eslint-disable-next-line max-len
        if (
          !detectionsFilterByScore[delIndex].visited &&
          det.class === detectionsFilterByScore[delIndex].class &&
          iou(det.roi, detectionsFilterByScore[delIndex].roi) > nms_thr
        ) {
          detectionsFilterByScore[delIndex].visited = true;
        }
      }
      return true;
    }
    return false;
  });
  return detectionsFilterByIOU;
}

function calculateReshapedDims(originalDims, shapeHints) {
  const nDims = shapeHints.length;
  const reshapedDims = Array(nDims).fill(-1);

  const unknownDimension = shapeHints.indexOf(-1);
  if (unknownDimension !== -1 && shapeHints.indexOf(-1, unknownDimension + 1) !== -1) {
    throw new Error('at most one dimension can be -1');
  }

  let knownSize = 1;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < nDims; ++i) {
    if (shapeHints[i] > 0) {
      knownSize *= shapeHints[i];
      reshapedDims[i] = shapeHints[i];
    } else if (shapeHints[i] === 0) {
      if (i >= originalDims.length) {
        throw new Error('the dimension with value zero exceeds the dimension size of the input tensor');
      }
      knownSize *= originalDims[i];
      reshapedDims[i] = originalDims[i];
    }
  }

  if (unknownDimension !== -1) {
    if (knownSize === 0 || calculateTensorSize(originalDims) % knownSize !== 0) {
      throw new Error(
        `the input tensor cannot be reshaped to the requested shape. Input shape: [${originalDims}] Output shape: [${shapeHints}]`,
      );
    }
    reshapedDims[unknownDimension] = calculateTensorSize(originalDims) / knownSize;
  }

  return reshapedDims;
}

function calculateTensorSize(dims) {
  return getSizeFromDimensionRange(dims, 0, dims.length);
}

function getSizeFromDimensionRange(dims, start, end) {
  let size = 1;
  // eslint-disable-next-line no-plusplus
  for (let i = start; i < end; ++i) {
    if (dims[i] <= 0) {
      throw new Error(
        'cannot get valid size from specified dimension range. Most likely the range contains 0 or negative values in them.',
      );
    }
    size *= dims[i];
  }
  return size;
}

function reshapeTensor(x, shape) {
  const reshapedDims = calculateReshapedDims(x.dims, shape);
  const output = new Tensor(x.type, createTypedArray(x.type, x.data.length), reshapedDims);
  const X = x.data;
  const Y = output.data;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < x.data.length; ++i) {
    Y[i] = X[i];
  }
  return output;
}

function createTypedArray(type, size) {
  switch (type) {
    case 'bool':
      return new Uint8Array(size);
    case 'int32':
      return new Int32Array(size);
    case 'float32':
      return new Float32Array(size);
    default:
      throw new Error('Unsupported type');
  }
}

function indicesToOffset(indices, strides) {
  const rank = strides.length;
  if (rank === 0) {
    return 0;
  }
  let index = indices[indices.length - 1];
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < indices.length - 1; ++i) {
    index += strides[i] * indices[i];
  }
  return index;
}

function getValueAtIndices(tensor, indices, strides) {
  return tensor.data[indicesToOffset(indices, strides)];
}

async function createInferenceSession(model) {
  let foundProvider = false;
  // const providers = ['webgl', 'wasm', 'cpu', 'xnnpack', 'cuda'];
  const providers = ['wasm'];
  const options = { enableCpuMemArena: true };
  const sessions = await Promise.all(
    providers.map(async (provider) => {
      try {
        const session = await InferenceSession.create(model, {
          executionProviders: [provider],
          ...options,
        });
        // const session = null;
        foundProvider = true;
        return session;
      } catch (error) {
        return null;
      }
    }),
  );

  if (!foundProvider) {
    throw new Error('No valid execution provider found');
  }

  return sessions.find((session) => session !== null);
}

function getTensorData(type, data, dims) {
  return new Tensor(type, data, dims);
}

export { classNms, reshapeTensor, getValueAtIndices, createInferenceSession, getTensorData };
